/**
 * Centralized Error Handler Service
 * Provides error formatting, recovery strategies, and user-friendly messages
 */

import {
  AppError,
  FrappeApiError,
  NetworkError,
  ValidationError,
  AuthenticationError,
  AuthorizationError,
} from "./ErrorTypes";
import { logger } from "@/lib/utils/logger";

export interface ErrorContext {
  operation?: string;
  resource?: string;
  userId?: string;
  timestamp?: Date;
  [key: string]: unknown;
}

export interface RecoveryStrategy {
  canRetry: boolean;
  retryAfter?: number; // milliseconds
  fallbackAction?: string;
  userMessage: string;
}

export class ErrorHandler {
  /**
   * Handle and format an error
   * @param error - The error to handle
   * @param context - Additional context about where the error occurred
   * @returns Formatted error information
   */
  static handle(error: unknown, context?: ErrorContext): {
    error: AppError;
    canRetry: boolean;
    recoveryStrategy: RecoveryStrategy;
    userMessage: string;
  } {
    const appError = this.normalizeError(error);
    const canRetry = this.canRetry(appError);
    const recoveryStrategy = this.getRecoveryStrategy(appError, context);
    const userMessage = this.formatForUser(appError, context);

    // Log error with context
    logger.error("Error handled:", {
      error: appError,
      context,
      canRetry,
      recoveryStrategy,
    });

    return {
      error: appError,
      canRetry,
      recoveryStrategy,
      userMessage,
    };
  }

  /**
   * Normalize unknown error to AppError
   */
  static normalizeError(error: unknown): AppError {
    if (error instanceof AppError) {
      return error;
    }

    if (error instanceof Error) {
      return new AppError(error.message, "UNKNOWN_ERROR", error);
    }

    return new AppError(
      String(error) || "An unknown error occurred",
      "UNKNOWN_ERROR",
      error
    );
  }

  /**
   * Determine if error is retryable
   */
  static canRetry(error: AppError): boolean {
    if (error instanceof NetworkError) {
      return true; // Network errors are usually retryable
    }

    if (error instanceof FrappeApiError) {
      // 5xx errors are retryable, 4xx are not (except 429 - rate limit)
      if (error.status === 429) {
        return true; // Rate limit - can retry after delay
      }
      return error.status !== undefined && error.status >= 500;
    }

    if (error instanceof AuthenticationError) {
      return false; // Auth errors require user action
    }

    if (error instanceof AuthorizationError) {
      return false; // Permission errors won't be fixed by retrying
    }

    if (error instanceof ValidationError) {
      return false; // Validation errors need user input correction
    }

    return false; // Default to not retryable
  }

  /**
   * Get recovery strategy for an error
   */
  static getRecoveryStrategy(
    error: AppError,
    _context?: ErrorContext
  ): RecoveryStrategy {

    if (error instanceof NetworkError) {
      return {
        canRetry: true,
        retryAfter: 1000, // Retry after 1 second
        userMessage: "Network error. Retrying...",
      };
    }

    if (error instanceof FrappeApiError) {
      if (error.status === 429) {
        return {
          canRetry: true,
          retryAfter: 5000, // Retry after 5 seconds for rate limit
          userMessage: "Too many requests. Please wait...",
        };
      }

      if (error.status && error.status >= 500) {
        return {
          canRetry: true,
          retryAfter: 2000, // Retry after 2 seconds for server errors
          userMessage: "Server error. Retrying...",
        };
      }

      return {
        canRetry: false,
        userMessage: error.message || "An error occurred",
      };
    }

    if (error instanceof AuthenticationError) {
      return {
        canRetry: false,
        fallbackAction: "redirect_to_login",
        userMessage: "Please log in again",
      };
    }

    if (error instanceof AuthorizationError) {
      return {
        canRetry: false,
        userMessage: "You don't have permission to perform this action",
      };
    }

    if (error instanceof ValidationError) {
      return {
        canRetry: false,
        userMessage: error.errors?.join(", ") || error.message,
      };
    }

    return {
      canRetry: false,
      userMessage: "An unexpected error occurred",
    };
  }

  /**
   * Format error message for user display
   */
  static formatForUser(error: AppError, context?: ErrorContext): string {
    // Use recovery strategy user message as it's already user-friendly
    const strategy = this.getRecoveryStrategy(error, context);
    return strategy.userMessage;
  }

  /**
   * Check if error is a specific type
   */
  static isType<T extends AppError>(
    error: unknown,
    errorClass: new (...args: any[]) => T
  ): error is T {
    return error instanceof errorClass;
  }

  /**
   * Extract error details for logging/debugging
   */
  static extractErrorDetails(error: unknown): {
    name: string;
    message: string;
    stack?: string;
    code?: string;
    status?: number;
    [key: string]: unknown;
  } {
    if (error instanceof AppError) {
      const details: any = {
        name: error.name,
        message: error.message,
        code: error.code,
      };

      if (error instanceof FrappeApiError) {
        details.status = error.status;
        details.statusText = error.statusText;
      }

      if (error instanceof ValidationError) {
        details.field = error.field;
        details.errors = error.errors;
      }

      if (error.stack) {
        details.stack = error.stack;
      }

      return details;
    }

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return {
      name: "Unknown",
      message: String(error),
    };
  }
}

